import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import { ReviewCard } from './ReviewCard';
import { RecoveryResponseEditor } from './RecoveryResponseEditor';
import { RecoveryConfiguration } from './RecoveryConfiguration';
import { RecoveryAnalytics } from './RecoveryAnalytics';
import { WeeklyScorecard } from './WeeklyScorecard';
import './ReviewInbox.css';

interface Props {
  storeId: string;
  storeName: string;
  initialFilter?: string;
}

interface Workflow {
  workflowId: string;
  storeId: string;
  platform: string;
  rating: number;
  reviewText: string;
  reviewerName: string;
  reviewTimestamp: string;
  tier: number;
  status: string;
  complaints: string[];
  posContext: any;
  slaDeadline: string;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'AI_READY', label: 'New' },
  { value: 'APPROVED', label: 'Sent' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'CLOSED', label: 'Closed' }
];

export const ReviewInbox: React.FC<Props> = ({ storeId, storeName, initialFilter }) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialFilter || '');
  const [tierFilter, setTierFilter] = useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'inbox' | 'config' | 'analytics' | 'scorecard'>('inbox');

  const fetchWorkflows = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const url = statusFilter
        ? API_ENDPOINTS.recoveryWorkflows(storeId, statusFilter)
        : API_ENDPOINTS.recoveryWorkflows(storeId);
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || []);
      }
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId, statusFilter]);

  useEffect(() => {
    fetchWorkflows();
    const interval = setInterval(fetchWorkflows, 30000);
    return () => clearInterval(interval);
  }, [fetchWorkflows]);

  const filteredWorkflows = tierFilter
    ? workflows.filter(w => w.tier === parseInt(tierFilter))
    : workflows;

  const overdueCount = workflows.filter(w => ['OVERDUE', 'MISSED'].includes(w.status)).length;
  const pendingCount = workflows.filter(w => w.status === 'AI_READY').length;

  const subTab = activeSubTab as string;

  const renderSubNav = () => (
    <div className="inbox-sub-nav">
      <button className={subTab === 'inbox' ? 'active' : ''} onClick={() => setActiveSubTab('inbox')}>
        Inbox {pendingCount > 0 && <span className="count-badge">{pendingCount}</span>}
      </button>
      <button className={subTab === 'config' ? 'active' : ''} onClick={() => setActiveSubTab('config')}>Configuration</button>
      <button className={subTab === 'analytics' ? 'active' : ''} onClick={() => setActiveSubTab('analytics')}>Analytics</button>
      <button className={subTab === 'scorecard' ? 'active' : ''} onClick={() => setActiveSubTab('scorecard')}>Scorecard</button>
    </div>
  );

  if (activeSubTab === 'config') {
    return (
      <div className="review-inbox">
        {renderSubNav()}
        <RecoveryConfiguration storeId={storeId} storeName={storeName} />
      </div>
    );
  }

  if (activeSubTab === 'analytics') {
    return (
      <div className="review-inbox">
        {renderSubNav()}
        <RecoveryAnalytics storeId={storeId} />
      </div>
    );
  }

  if (activeSubTab === 'scorecard') {
    return (
      <div className="review-inbox">
        {renderSubNav()}
        <WeeklyScorecard storeId={storeId} />
      </div>
    );
  }

  return (
    <div className="review-inbox">
      {renderSubNav()}

      <div className="inbox-header">
        <h3>Guest Recovery Inbox</h3>
        <div className="inbox-stats">
          <span className="stat">{pendingCount} pending</span>
          {overdueCount > 0 && <span className="stat overdue">{overdueCount} overdue</span>}
          <span className="stat">{workflows.length} total</span>
        </div>
      </div>

      <div className="inbox-filters">
        <div className="filter-group">
          <label>Status:</label>
          <div className="filter-buttons">
            {STATUS_FILTERS.map(f => (
              <button key={f.value} className={`filter-btn ${statusFilter === f.value ? 'active' : ''}`} onClick={() => setStatusFilter(f.value)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <label>Tier:</label>
          <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
            <option value="">All Tiers</option>
            <option value="1">Tier 1 - Minor</option>
            <option value="2">Tier 2 - Moderate</option>
            <option value="3">Tier 3 - Severe</option>
            <option value="4">Tier 4 - Safety</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="inbox-loading">Loading recovery workflows...</div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="inbox-empty">
          <i className="fas fa-check-circle"></i>
          <p>No recovery workflows match your filters</p>
        </div>
      ) : (
        <div className="inbox-list">
          {filteredWorkflows.map(w => (
            <ReviewCard key={w.workflowId} workflow={w} onClick={setSelectedWorkflowId} />
          ))}
        </div>
      )}

      {selectedWorkflowId && (
        <RecoveryResponseEditor
          workflowId={selectedWorkflowId}
          onClose={() => setSelectedWorkflowId(null)}
          onRefresh={fetchWorkflows}
        />
      )}
    </div>
  );
};
